import { z } from 'zod'

/**
 * Modifier groups attached to an item.
 *
 * Two kinds exist:
 * - `base` (static): the editor owns the full structure inline, stored
 *   in WP post meta as `crb_modifier_group`. This is what 99% of
 *   sites actually use.
 * - `dynamic`: group references an external taxonomy
 *   (`ezmodgroup` / `ezmod`) and resolves at render time. Almost
 *   never used in production; we surface but don't edit it.
 *
 * In code we keep `type` exposed so future dynamic editing can be
 * dropped in without rewriting the data shape.
 */
export type ModifierGroupType = 'base' | 'dynamic'

export const ModifierSchema = z.object({
  name: z.string(),
  /** Price kept as a string — WP returns it formatted (e.g. "1.50") and may be empty. */
  price: z.string(),
})
export type Modifier = z.infer<typeof ModifierSchema>

export const ModifierGroupSchema = z.object({
  type: z
    .union([z.literal('base'), z.literal('dynamic'), z.string()])
    .transform((s): ModifierGroupType => (s === 'dynamic' ? 'dynamic' : 'base')),
  name: z.string(),
  desc: z.string(),
  /** Optional flat surcharge for picking any modifier in the group. */
  groupPrice: z.string(),
  modifiers: z.array(ModifierSchema),
})
export type ModifierGroup = z.infer<typeof ModifierGroupSchema>
